import React, { useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers";
import * as dayjs from "dayjs";
import IntlMessages from "util/IntlMessages";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid
} from "@material-ui/core";
import FormInput from "components/Form/Controls/input";
import FormSelectAutoComplete from "components/Form/Controls/select-autocomplete";
import FormDatePicker from "components/Form/Controls/datepicker";
import ButtonAsync from "components/Form/Controls/ButtonAsync";

const LedagaForm = (props) => {
  const {
    rowObj,
    personId,
    openCRU,
    handleRequestClose,
    handleCreateUpdate,
    readOnly,
    boolList,
    lesionSideList,
    locationLesionsList,
    amountMedicationList,
  } = props;

  const getCboValue = (items, id) => {
    if (typeof id == "object") {
      return id;
    }
    const result = items.find((x) => x.id === id);
    return result ? { label: result.name, value: result.id } : "";
  };

  const defaultValues = {
    consultationDate: null,
    mswat: "",
    lesionSide: "",
    lesionLocation: "",
    clormetina: "",
    lastApplicationDate: null,
    tubeExpiredDate: null,
    amount: ""
  };

  const validationSchema = yup.object().shape({
    consultationDate: yup
      .date()
      .nullable()
      .typeError(<IntlMessages id="yup.InvalidDate" />)
      .required(<IntlMessages id="core.fieldRequired" />),

    mswat: yup
      .number()
      .typeError(<IntlMessages id="yup.numericValidation" />)
      .required(<IntlMessages id="core.fieldRequired" />)
      .max(100, <IntlMessages id="core.valueMax" />),
    lesionSide: yup
      .object()
      .shape({
        value: yup.number().required(<IntlMessages id="core.fieldRequired" />)
      })
      .nullable()
      .required(<IntlMessages id="core.fieldRequired" />),

    lesionLocation: yup
      .object()
      .shape({
        value: yup.number().required(<IntlMessages id="core.fieldRequired" />)
      })
      .nullable()
      .required(<IntlMessages id="core.fieldRequired" />),

    clormetina: yup
      .object()
      .shape({
        value: yup.number().required(<IntlMessages id="core.fieldRequired" />) 
      })
      .nullable()
      .required(<IntlMessages id="core.fieldRequired" />),

    lastApplicationDate: yup
      .date()
      .nullable()
      .typeError(<IntlMessages id="yup.InvalidDate" />),

    tubeExpiredDate: yup
      .date()
      .nullable()
      .typeError(<IntlMessages id="yup.InvalidDate" />),

    amount: yup
      .object()
      .shape({
        value: yup.number().nullable()
      })
      .nullable(),
  });

  const methods = useForm({
    defaultValues,
    resolver: yupResolver(validationSchema),
  });

  const { handleSubmit, setValue, errors } = methods;

  useEffect(() => {
    if (openCRU) {
      setTimeout(() => loadData(rowObj), 150);
    }
  }, [openCRU]);

  const loadData = (rowObj) => {
    setValue("consultationDate", rowObj && rowObj.consultationDate ? dayjs(rowObj.consultationDate) : null);
    setValue("mswat", rowObj && rowObj.mswat ? rowObj.mswat : "");
    setValue("lesionSide",
      rowObj && rowObj.lesionSide
      ? getCboValue(lesionSideList, rowObj.lesionSide)
      : null);
     setValue("lesionLocation",
    rowObj && rowObj.lesionLocation
      ? getCboValue(locationLesionsList, rowObj.lesionLocation)
      : null
  );
    setValue("clormetina", rowObj && rowObj.clormetina !== null && rowObj.clormetina !== undefined
      ? getCboValue(boolList, rowObj.clormetina ? 1 : 0)
      : null);
    setValue("lastApplicationDate", rowObj && rowObj.lastApplicationDate ? dayjs(rowObj.lastApplicationDate) : null);
    setValue("tubeExpiredDate", rowObj && rowObj.tubeExpiredDate ? dayjs(rowObj.tubeExpiredDate) : null);
    setValue(
      "amount",
      rowObj && rowObj.amount != null && rowObj.amount > 0
        ? getCboValue(amountMedicationList, rowObj.amount)
        : null
    );
  };

  const onSubmit = async (d) => {
    const obj = {
      ...rowObj,
      id: !rowObj ? 0 : rowObj.id,
      personId: personId,
      consultationDate: dayjs(d.consultationDate).format("YYYY-MM-DDTHH:mm:ss"),
      mswat: Number(d.mswat),
      lesionSide: d.lesionSide.value,
      lesionLocation: d.lesionLocation.value,
      clormetina: d.clormetina.value == 1 ? true : false,
      lastApplicationDate: d.lastApplicationDate
        ? dayjs(d.lastApplicationDate).format("YYYY-MM-DDTHH:mm:ss")
        : null,
      tubeExpiredDate: d.tubeExpiredDate
        ? dayjs(d.tubeExpiredDate).format("YYYY-MM-DDTHH:mm:ss")
        : null,
      amount: d.amount && d.amount.value != null ? d.amount.value : null,
    };
    await handleCreateUpdate(obj);
  };

  const disabled = false;
  return (
    <Dialog
      fullWidth={true}
      maxWidth="md"
      open={openCRU}
      disableBackdropClick={true}
      onClose={handleRequestClose}
    >
      <DialogTitle>
        <IntlMessages id={!rowObj ? "core.add" : "core.edit"} />
      </DialogTitle>
      <DialogContent>
        <div className="col-xl-12 col-lg-12 col-md-12 col-12">
          <FormProvider {...methods}>
            <form className="row" noValidate autoComplete="off">
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormDatePicker
                    name="consultationDate"
                    label="Fecha consulta con el médico"
                    format="YYYY-MM-DD"
                    disabled={disabled}
                    margin="normal"
                    required={true}
                    errorobj={errors}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormInput
                    name="mswat"
                    label="% MSWAT"
                    disabled={disabled}
                    margin="normal"
                    required={true}
                    errorobj={errors}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormSelectAutoComplete
                    name="lesionSide"
                    label="Costado de la localización de las lesiones"
                    options={lesionSideList}
                    margin="normal"
                    disabled={disabled}
                    required={true}
                    errorobj={errors}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormSelectAutoComplete
                    name="lesionLocation"
                    label="Localización de las lesiones"
                    options={locationLesionsList}
                    disabled={disabled}
                    margin="normal"
                    required={true}
                    errorobj={errors}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormSelectAutoComplete
                    name="clormetina"
                    label="¿Se aplica clormetina en esta lesión?"
                    options={boolList}
                    disabled={disabled}
                    margin="normal"
                    required={true}
                    errorobj={errors}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormDatePicker
                    name="lastApplicationDate"
                    label="Fecha última aplicación"
                    format="YYYY-MM-DD"
                    disabled={disabled}
                    margin="normal"
                    required={false}
                    errorobj={errors}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormDatePicker
                    name="tubeExpiredDate"
                    label="Fecha de vencimiento tubo"
                    format="YYYY-MM-DD"
                    disabled={disabled}
                    margin="normal"
                    required={false}
                    errorobj={errors}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormSelectAutoComplete
                    name="amount"
                    label="Cantidad de medicamento en stock Tubo/s - días"
                    options={amountMedicationList}
                    disabled={disabled}
                    margin="normal"
                    required={false}
                    errorobj={errors}
                  />
                </Grid>
              </Grid>
            </form>
          </FormProvider>
          {!disabled && (
            <div className="col-lg-12 col-md-12 col-xs-12 text-center mt-4">
              <ButtonAsync
                onClick={handleSubmit(onSubmit)}
                variant="contained"
                color="primary"
              >
                <IntlMessages id="core.save" />
              </ButtonAsync>
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleRequestClose} color="secondary">
          <IntlMessages id="core.close" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LedagaForm;
